// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { getSettingsCollection } from "@eisinc/App/Store/Selectors/SettingsSelectors";
import Button from "@eisinc/MobiusOverrides/Button";
import { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@eisinc/Overrides/Components/ProductUnitOfMeasureSelect";
import { HasIsLoading } from "@eisinc/XDL/types";
import { mergeWithCss } from "@eisinc/XDL/Utilities/mergeWithCss";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductActionsProps {
    carouselId?: string;
    iconOnly?: boolean;
    showActions?: boolean;
    alwaysShowQty?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    currentPage?: string;
    disableUOM?: boolean;
    hideUOM?: boolean;
    isPDP?: boolean;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
}

type Props = ProductActionsProps & HasProductContext & HasIsLoading;

export interface ProductActionsStyle {
    wrapper?: InjectableCss;
    qtyWrapper?: GridItemProps;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    uomSelectWrapper?: InjectableCss;
    uomSelect?: SelectPresentationProps;
    multipleSaleQty?: InjectableCss;
    selectOptionsButton?: ButtonPresentationProps;
}

export const productActionsStyle: ProductActionsStyle = {
    productAddToCartButtonStyles: {
        sizeVariant: "small",
    },
    uomSelect: {
        sizeVariant: "small",
    },
    selectOptionsButton: {
        css: css`
            width: 100%;
        `,
    },
};

const ProductActions: FC<Props> = ({
    productContext,
    carouselId,
    extendedStyles,
    onAddToCartClick,
    iconOnly,
    hideUOM,
    isLoading,
    disableUOM,
    isPDP,
    configurationCompleted,
    configurationSelection,
    subscription,
    variantSelectionCompleted,
}) => {
    const history = useHistory();
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    const uomRef = React.useRef<HTMLDivElement>(null);

    const { productSettings, customerType } = useAppSelector(state => {
        return {
            productSettings: getSettingsCollection(state).productSettings,
            customerType: getSession(state).properties?.["mpExistingCustomer"],
        };
    });

    const unitOfMeasures = isLoading ? [] : productContext.product.unitOfMeasures;
    const willShowUnitOfMeasureSelect =
        !hideUOM &&
        !(
            !productSettings?.alternateUnitsOfMeasure ||
            !unitOfMeasures ||
            unitOfMeasures.filter((uom: any) => uom.unitOfMeasure).length <= 1
        );

    const handleSelectOptionsClick = () => {
        (window as any)?._MpProductCarouselClick(productContext, customerType);
        history.push(productContext.productInfo.productDetailPath);
    };

    return productContext.product.isVariantParent && !isPDP ? (
        <Button
            {...styles.selectOptionsButton}
            onClick={handleSelectOptionsClick}
            variant="primary"
            sizeVariant="small"
        >
            {translate("Select Options")}
        </Button>
    ) : (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector="product_actions"
            {...{ iconOnly, hasUOM: willShowUnitOfMeasureSelect }}
        >
            <StyledWrapper {...styles.qtyWrapper}>
                {isLoading ? (
                    <Skeleton height="26px" />
                ) : (
                    <>
                        <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                            {translate("QTY")}
                        </VisuallyHidden>
                        <ProductQuantityOrdered
                            alwaysVisible={isPDP}
                            configurationSelection={configurationSelection}
                            configurationCompleted={configurationCompleted}
                            variantSelectionCompleted={variantSelectionCompleted}
                            labelOverride=""
                            data-test-selector="productQty"
                        />
                    </>
                )}
            </StyledWrapper>

            {willShowUnitOfMeasureSelect && (
                <StyledWrapper {...styles.uomSelectWrapper} ref={uomRef}>
                    {isLoading ? (
                        <Skeleton height="26px" />
                    ) : (
                        <ProductUnitOfMeasureSelect {...styles.uomSelect} disabled={disableUOM} showLabel={false} />
                    )}
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.addToCartButtonWrapper} {...{ hasUom: willShowUnitOfMeasureSelect, iconOnly }}>
                {isLoading ? (
                    <Skeleton height="26px" />
                ) : (
                    <ProductAddToCartButton
                        onAddToCartClick={onAddToCartClick}
                        extendedStyles={styles.productAddToCartButtonStyles}
                        data-test-selector="addToCartBtn"
                        alwaysVisible={isPDP}
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        subscription={subscription}
                        {...{
                            iconOnly,
                            tooltip: productContext.productInfo.unitOfMeasure ? "" : translate("Select UOM first"),
                        }}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductActions);
